import { useEffect, useLayoutEffect, useRef } from 'react';
import { gsap } from '@/lib/gsap';
import { multiplier, multiplierLabel } from '@/lib/score';
import { useAppStore } from '@/lib/store';

export function ComboCounter(): JSX.Element {
  const combo = useAppStore((s) => s.combo.combo);
  const lastGrade = useAppStore((s) => s.combo.lastGrade);
  const seq = useAppStore((s) => s.combo.burstSeq);

  const rootRef = useRef<HTMLDivElement>(null);
  const numRef = useRef<HTMLDivElement>(null);
  const tierRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (root === null) return;
    if (combo > 0) {
      gsap.fromTo(root,
        { x: 32, opacity: 0, scale: 0.6 },
        { x: 0, opacity: 1, scale: 1, duration: 0.45, ease: 'back.out(2.4)' },
      );
    } else {
      gsap.to(root, { x: 24, opacity: 0, scale: 0.6, duration: 0.3, ease: 'power2.in' });
    }
  }, [combo > 0]);

  useEffect(() => {
    const num = numRef.current;
    if (num === null) return;
    if (combo > 0 && lastGrade !== 'miss') {
      gsap.fromTo(
        num,
        { scale: 1.8, rotate: -6, filter: 'brightness(1.6)' },
        { scale: 1, rotate: 0, filter: 'brightness(1)', duration: 0.55, ease: 'elastic.out(1, 0.35)' },
      );
    } else if (lastGrade === 'miss' && combo === 0) {
      gsap.fromTo(rootRef.current, { x: 0 }, {
        keyframes: [
          { x: -6, duration: 0.04 },
          { x: 5, duration: 0.04 },
          { x: -3, duration: 0.04 },
          { x: 2, duration: 0.04 },
          { x: 0, duration: 0.06 },
        ],
        ease: 'none',
      });
    }
  }, [seq, combo, lastGrade]);

  useEffect(() => {
    const tier = tierRef.current;
    if (tier === null) return;
    if (multiplier(combo) > 1) {
      gsap.fromTo(
        tier,
        { x: 24, opacity: 0, scale: 0.7 },
        { x: 0, opacity: 1, scale: 1, duration: 0.5, ease: 'back.out(2)' },
      );
    }
  }, [combo]);

  const m = multiplier(combo);
  const colorClass = m === 5 ? 'text-mint-400' : m === 3 ? 'text-ember-400' : 'text-honey-400';
  const tierColorClass =
    m === 5 ? 'text-mint-300 border-mint-400/60 glow-lime'
    : m === 3 ? 'text-ember-300 border-ember-400/60 glow-pink'
    : 'text-honey-300 border-honey-400/60 glow-cyan';

  return (
    <div
      ref={rootRef}
      // top-28 on mobile because the AppShell renders a 2nd-row tab strip
      // (mobile nav). On md+ the nav is single-row so top-20 works.
      className="pointer-events-none fixed top-28 right-3 md:top-20 md:right-8 z-40 select-none flex flex-col items-end gap-1 max-w-[160px]"
      style={{ opacity: 0 }}
    >
      <div ref={numRef} className={`font-score text-5xl md:text-6xl leading-none text-neon ${colorClass}`}>
        ×{combo}
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cream-300/90 font-arcade">COMBO</div>
      {m > 1 && (
        <div ref={tierRef} className={`text-xs font-arcade px-2 py-1 rounded border ${tierColorClass}`}>
          {multiplierLabel(m)}
        </div>
      )}
    </div>
  );
}
